import React, {useState} from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import {Draggable, LockStyle, Text, FontVariant} from '@pexip/components';

import type {CallHistoryFavoriteItem} from '../../types';
import {CallHistoryItemView} from '../CallHistoryItem/CallHistoryItem.view';

import styles from './FavoriteCallHistoryItem.module.scss';

export const FavoriteCallHistoryItemView: React.FC<{
    item: CallHistoryFavoriteItem;
    isMobile: boolean;
    showDivider: boolean;
    isDragDisabled: boolean;
    scrollElementRef: React.RefObject<HTMLElement | null>;
    style?: React.CSSProperties;
    onPointerMoveExtra: (draggedElement: HTMLDivElement) => void;
    onPointerUpExtra: (draggedElement: HTMLDivElement) => void;
    onClick: () => void;
    handleRemoveFavorite: () => void;
    handleReorderByKeyboard?: (direction: 'up' | 'down') => void;
}> = ({
    item,
    isMobile,
    showDivider,
    isDragDisabled,
    scrollElementRef,
    style,
    onPointerMoveExtra,
    onPointerUpExtra,
    onClick,
    handleRemoveFavorite,
    handleReorderByKeyboard,
}) => {
    const {t} = useTranslation();
    const [dragCaptureTarget, setDragCaptureTarget] =
        useState<HTMLElement | null>(null);

    const removeFavoriteDisabledReason = item.isBranding ? (
        <Text
            htmlTag="p"
            className={styles.brandingFavoriteDisabledReason}
            fontVariant={FontVariant.Small}
            variant="tinted"
        >
            {t(
                'media.call-history.remove-favorite-disabled-branding',
                'Added by your admin and can not be removed.',
            )}
        </Text>
    ) : undefined;

    return (
        <Draggable
            id={item.meetingId}
            scrollElementRef={scrollElementRef}
            pointerCapture
            pointerCaptureTarget={dragCaptureTarget}
            lockStyle={LockStyle.VERTICAL}
            onPointerMoveExtra={onPointerMoveExtra}
            onPointerUpExtra={onPointerUpExtra}
            isDisabled={isDragDisabled}
            shouldCaptureClick={false}
            style={style}
            className={cx(styles.draggableItem, {
                [styles.itemDivider]: showDivider,
            })}
        >
            <CallHistoryItemView
                onClick={onClick}
                handleCallMeetingId={onClick}
                handleRemoveFavorite={handleRemoveFavorite}
                isRemoveFavoriteDisabled={item.isBranding}
                removeFavoriteDisabledReason={removeFavoriteDisabledReason}
                title={item.title}
                meetingId={item.meetingId}
                imageUrl={item.avatarImageUrl}
                isMobile={isMobile}
                draggableHandleAriaLabel={t(
                    'media.call-history.reorder-favorite-drag-handle',
                    'Drag handle to reorder favorite',
                )}
                handleReorderByKeyboard={handleReorderByKeyboard}
                setDragCaptureTarget={setDragCaptureTarget}
            />
        </Draggable>
    );
};

export type FavoriteCallHistoryItemViewProps = React.ComponentProps<
    typeof FavoriteCallHistoryItemView
>;
